import React, { useState, useEffect } from 'react';
import { User, AlertTriangle, Check, Lock, X, Loader2 } from 'lucide-react';
import timetableService from '../../services/TimetableService.js';

const SubjectAssignmentDialog = ({ 
  show, 
  onClose, 
  targetSlot, // { dayOfWeek, timeKey, slotLabel, startTime, endTime, currentSlot, timetableId, academicYear }
  subjectAllocations = [], 
  teachers = [],
  scheduledCounts = {},
  onAssignSlot,
  onClearSlot
}) => {
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedAlloc, setSelectedAlloc] = useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [conflictWarning, setConflictWarning] = useState(null);
  const [checkingConflict, setCheckingConflict] = useState(false);

  const safeAllocations = (subjectAllocations || []).filter(Boolean);
  const safeTeachers = (teachers || []).filter(Boolean);

  // Helper to extract a deterministic ID string for teacher
  const getTeacherKey = (t, idx = 0) => {
    if (!t) return `TCH-${idx}`;
    return String(t.teacherId || t.id || t._id || t.teacherCode || t.teacherName || t.name || `TCH-${idx}`);
  };

  // Helper to extract allocation ID string
  const getAllocKey = (alloc, idx = 0) => {
    if (!alloc) return `ALLOC-${idx}`;
    return String(alloc.subjectId || alloc.id || alloc.subjectCode || `ALLOC-${idx}`);
  };

  // 1. Initial selection when dialog opens
  useEffect(() => {
    if (!show || !targetSlot) {
      setSelectedSubjectId('');
      setSelectedTeacherId('');
      setSelectedAlloc(null);
      setConflictWarning(null);
      return;
    }

    if (targetSlot.currentSlot && (targetSlot.currentSlot.subjectId || targetSlot.currentSlot.subject)) {
      const curSubId = String(targetSlot.currentSlot.subjectId || '');
      const curSubName = String(targetSlot.currentSlot.subject || '').toLowerCase();
      
      const matched = safeAllocations.find(a => 
        (curSubId && (String(a.subjectId) === curSubId || String(a.id) === curSubId)) ||
        (curSubName && (String(a.subjectName || '').toLowerCase() === curSubName || String(a.subjectCode || '').toLowerCase() === curSubName))
      );

      if (matched) {
        setSelectedSubjectId(getAllocKey(matched));
      } else if (curSubId) {
        setSelectedSubjectId(curSubId);
      }

      if (targetSlot.currentSlot.teacherId) {
        setSelectedTeacherId(String(targetSlot.currentSlot.teacherId));
      }
    } else if (safeAllocations.length > 0) {
      // Find first selectable subject allocation (that is not completed)
      const firstValid = safeAllocations.find(a => {
        if (!a) return false;
        const req = a.periodsPerWeek || 5;
        const subName = a.subjectName || a.subjectCode || '';
        const sched = scheduledCounts[subName] || 0;
        return sched < req;
      });

      const chosen = firstValid || safeAllocations[0];
      setSelectedSubjectId(getAllocKey(chosen));
    } else {
      setSelectedSubjectId('');
      setSelectedTeacherId('');
    }
    setConflictWarning(null);
  }, [show, targetSlot, subjectAllocations]);

  // 2. Resolve selected allocation and associated default teacher
  useEffect(() => {
    if (!selectedSubjectId || safeAllocations.length === 0) {
      setSelectedAlloc(null);
      return;
    }

    const found = safeAllocations.find(a => 
      getAllocKey(a) === String(selectedSubjectId) ||
      String(a.subjectId) === String(selectedSubjectId) ||
      String(a.id) === String(selectedSubjectId) ||
      String(a.subjectName) === String(selectedSubjectId) ||
      String(a.subjectCode) === String(selectedSubjectId)
    );

    setSelectedAlloc(found || safeAllocations[0] || null);

    if (found) {
      // Resolve teacher from allocation or match with safeTeachers
      let tId = found.teacherId;
      if (safeTeachers.length > 0) {
        if (tId) {
          const matchById = safeTeachers.find(t => String(t.teacherId || t.id || t._id) === String(tId));
          if (matchById) {
            setSelectedTeacherId(getTeacherKey(matchById));
            return;
          }
        }
        if (found.teacherName && found.teacherName !== 'Unassigned' && found.teacherName !== 'Not Assigned') {
          const matchByName = safeTeachers.find(t => {
            const name1 = String(t.teacherName || t.name || t.fullName || '').toLowerCase().trim();
            const name2 = String(found.teacherName || '').toLowerCase().trim();
            return name1 === name2 || name1.includes(name2) || name2.includes(name1);
          });
          if (matchByName) {
            setSelectedTeacherId(getTeacherKey(matchByName));
            return;
          }
        }
        setSelectedTeacherId(getTeacherKey(safeTeachers[0], 0));
      } else {
        setSelectedTeacherId(String(tId || ''));
      }
    }
  }, [selectedSubjectId, subjectAllocations, safeTeachers]);

  // 3. Check teacher conflict when selected teacher changes (Advisory UX)
  useEffect(() => {
    if (selectedTeacherId && targetSlot && safeTeachers.length > 0) {
      const activeT = safeTeachers.find((t, idx) => 
        getTeacherKey(t, idx) === String(selectedTeacherId) ||
        String(t.teacherId || t.id || t._id) === String(selectedTeacherId)
      );
      if (activeT) {
        checkConflict(activeT);
      }
    }
  }, [selectedTeacherId, targetSlot, safeTeachers]);

  const checkConflict = async (teacherObj) => {
    if (!teacherObj || !targetSlot) return;
    try {
      setCheckingConflict(true);
      setConflictWarning(null);
      const res = await timetableService.checkTeacherConflict({
        academicYear: targetSlot.academicYear || '2026-2027',
        teacherId: teacherObj.teacherId || teacherObj.id || teacherObj._id || '',
        teacherName: teacherObj.teacherName || teacherObj.name || teacherObj.fullName || '',
        dayOfWeek: targetSlot.dayOfWeek,
        startTime: targetSlot.startTime,
        endTime: targetSlot.endTime,
        timetableId: targetSlot.timetableId
      });

      if (res && res.hasConflict) {
        setConflictWarning(res.message);
      }
    } catch (err) {
      console.warn('Teacher conflict advisory check skipped:', err);
    } finally {
      setCheckingConflict(false);
    }
  };

  if (!show || !targetSlot) return null;

  // Resolve active teacher object
  const activeTeacher = safeTeachers.find((t, idx) => 
    getTeacherKey(t, idx) === String(selectedTeacherId) ||
    String(t.teacherId || t.id || t._id) === String(selectedTeacherId) ||
    String(t.teacherName || t.name || t.fullName) === String(selectedTeacherId)
  ) || (selectedAlloc ? {
    teacherName: selectedAlloc.teacherName || 'Default Faculty',
    teacherId: selectedAlloc.teacherId || ''
  } : null);

  const reqPeriods = selectedAlloc ? (selectedAlloc.periodsPerWeek || 5) : 5;
  const schedPeriods = selectedAlloc ? (scheduledCounts[selectedAlloc.subjectName || selectedAlloc.subjectCode] || 0) : 0;
  const isQuotaReached = selectedAlloc && !targetSlot.currentSlot?.subject && (schedPeriods >= reqPeriods);

  const handleConfirm = () => {
    if (!selectedAlloc) {
      console.warn('Cannot assign: No subject allocation found.');
      return;
    }

    const tName = activeTeacher?.teacherName || activeTeacher?.name || activeTeacher?.fullName || selectedAlloc.teacherName || 'Not Assigned';
    const tId = activeTeacher?.teacherId || activeTeacher?.id || activeTeacher?._id || selectedAlloc.teacherId || '';

    const slotPayload = {
      subjectId: selectedAlloc.subjectId || selectedAlloc.id || null,
      subject: selectedAlloc.subjectName || selectedAlloc.subjectCode || 'Subject',
      subjectCode: selectedAlloc.subjectCode || '',
      subjectAllocationId: selectedAlloc.id || null,
      bellScheduleSlotId: targetSlot.bellScheduleSlotId || null,
      slotOrder: targetSlot.slotOrder || null,
      teacherId: tId,
      teacher: tName,
      startTime: targetSlot.startTime,
      endTime: targetSlot.endTime,
      slotType: 'TEACHING'
    };

    console.log('[Timetable] Assigning slot:', targetSlot.dayOfWeek, targetSlot.timeKey, slotPayload);
    onAssignSlot(targetSlot.dayOfWeek, targetSlot.timeKey, slotPayload);
    onClose();
  };

  return (
    <div className="fs-modal-overlay" style={{ zIndex: 1100 }}>
      <div className="fs-modal-card" style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
        
        {/* HEADER */}
        <div className="fs-header" style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
              Add Subject
            </h3>
            <div style={{ fontSize: '13px', color: '#2563EB', fontWeight: '600', marginTop: '3px' }}>
              {targetSlot.dayOfWeek} • {targetSlot.slotLabel} ({targetSlot.startTime} – {targetSlot.endTime})
            </div>
          </div>
          <button className="fs-close-btn" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div className="fs-body" style={{ padding: '20px 24px' }}>
          
          {/* 1. SELECT SUBJECT */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px', display: 'block' }}>
              Select Subject <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <select
              className="form-control"
              value={selectedSubjectId}
              onChange={(e) => setSelectedSubjectId(e.target.value)}
              style={{ height: '44px', borderRadius: '8px', width: '100%', fontSize: '13.5px', fontWeight: '600', border: '1px solid #CBD5E1', padding: '0 12px' }}
            >
              {safeAllocations && safeAllocations.length > 0 ? (
                safeAllocations.map((alloc, idx) => {
                  if (!alloc) return null;
                  const allocKey = getAllocKey(alloc, idx);
                  const req = alloc.periodsPerWeek || 5;
                  const subName = alloc.subjectName || alloc.subjectCode || 'Subject';
                  const sched = scheduledCounts[subName] || 0;
                  const rem = req - sched;
                  const isDone = sched >= req && targetSlot.currentSlot?.subject !== subName;

                  let label = `${subName}`;
                  if (isDone) {
                    label += ` (${sched} of ${req} scheduled • Completed)`;
                  } else {
                    label += ` (${sched} of ${req} scheduled • ${rem} remaining)`;
                  }

                  return (
                    <option 
                      key={alloc.id || alloc.subjectId || idx} 
                      value={allocKey}
                    >
                      {label}
                    </option>
                  );
                })
              ) : (
                <option value="">-- No allocated subjects available for this Class --</option>
              )}
            </select>
          </div>

          {/* 2. SELECT TEACHER (ALL REGISTERED FACULTY) */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155' }}>
                Assigned Faculty Member <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {checkingConflict && (
                  <span style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Loader2 size={11} className="animate-spin" /> checking...
                  </span>
                )}
                <span style={{ fontSize: '11px', color: '#2563EB', fontWeight: '600' }}>
                  {safeTeachers.length > 0 ? `${safeTeachers.length} Faculty Registered` : ''}
                </span>
              </div>
            </div>
            <select
              className="form-control"
              value={selectedTeacherId}
              onChange={(e) => setSelectedTeacherId(e.target.value)}
              style={{ height: '44px', borderRadius: '8px', width: '100%', fontSize: '13.5px', fontWeight: '600', border: '1px solid #CBD5E1', padding: '0 12px' }}
            >
              {safeTeachers && safeTeachers.length > 0 ? (
                safeTeachers.map((t, idx) => {
                  if (!t) return null;
                  const tKey = getTeacherKey(t, idx);
                  const tName = t.teacherName || t.name || t.fullName || 'Teacher';
                  const tDesig = t.designation || t.specialization || t.department || '';
                  return (
                    <option key={tKey || idx} value={tKey}>
                      {tName} {tDesig ? `(${tDesig})` : ''}
                    </option>
                  );
                })
              ) : (
                <option value={selectedAlloc?.teacherId || ''}>
                  {selectedAlloc?.teacherName || 'Default Faculty'}
                </option>
              )}
            </select>
          </div>

          {/* QUOTA STATUS CARD */}
          {selectedAlloc && (
            <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '600' }}>Weekly Subject Quota</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: schedPeriods >= reqPeriods ? '#166534' : '#2563EB' }}>
                  {schedPeriods} of {reqPeriods} periods scheduled
                </span>
              </div>
            </div>
          )}

          {/* WARNING: Teacher Conflict Detected (Advisory) */}
          {conflictWarning && (
            <div style={{ padding: '12px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px', fontSize: '12.5px', color: '#991B1B', fontWeight: '600', marginBottom: '18px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>Teacher Conflict Notice:</strong>
                <div style={{ marginTop: '2px', fontWeight: '500' }}>{conflictWarning}</div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="fs-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', backgroundColor: '#F8FAFC' }}>
          <div>
            {targetSlot.currentSlot && targetSlot.currentSlot.subject && (
              <button 
                type="button" 
                style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' }}
                onClick={() => {
                  onClearSlot(targetSlot.dayOfWeek, targetSlot.timeKey);
                  onClose();
                }}
              >
                Clear Slot
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              type="button" 
              className="fs-btn-secondary" 
              onClick={onClose} 
              style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #CBD5E1', background: 'white', fontWeight: '600', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="fs-btn-primary" 
              disabled={!selectedAlloc}
              onClick={handleConfirm}
              style={{ 
                padding: '8px 20px', 
                borderRadius: '8px', 
                border: 'none', 
                background: '#2563EB', 
                color: 'white', 
                fontWeight: '700', 
                cursor: selectedAlloc ? 'pointer' : 'not-allowed', 
                opacity: selectedAlloc ? 1 : 0.6 
              }}
            >
              Add Subject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectAssignmentDialog;
